<template>
  <div class="wrapper">
    <div
      ref="frame"
      class="frame"
      @mousedown="onTouchstart"
      @touchstart="onTouchstart"
    >
      <div
        ref="slides"
        class="slides"
        :style="{ transform: `translate3d(${coordinate}px, 0, 0 )` }"
      >
        <slot class="test"></slot>
      </div>
    </div>
  </div>
</template>

<script>
// TODO: on sliders amount change, update slider
// TODO: move arrows outside
// TODO: add events
// TODO: we should somehow disable arrows or no ???
import throttle from "lodash.throttle";

const DEFAULT = {
  slideSpeed: 500,
  rewindSpeed: 1200
};

let touchOffset, isScrolling, delta;

export default {
  name: "AwesomeSlider",
  props: {
    rewind: Boolean,
    infinite: Number,
    enableMouseEvents: {
      type: Boolean
    }
  },
  data() {
    return {
      coordinate: 0,
      slides: null,
      slidesWidth: null,
      frameWidth: null,
      activeSlide: 0,
      transitionEndCallback: null,
      isTransitioning: false
    };
  },
  computed: {
    lastIndex() {
      return this.slides && this.slides.length - 1;
    },
    isLastSlide() {
      return this.activeSlide === this.lastIndex;
    },
    resetThrottled() {
      return throttle(this.reset, 500);
    }
  },
  mounted() {
    this.setup();
  },
  destroyed() {
    this.destroy();
  },
  methods: {
    setup() {
      window.addEventListener("touchmove", function() {}); // iOs scroll bug - https://github.com/metafizzy/flickity/issues/457#issuecomment-254501356
      window.addEventListener("resize", this.resetThrottled, { passive: true });
      this.slides = Array.prototype.slice.call(this.$refs.slides.children);
      if (this.infinite) {
        this.setInfinite();
      }
      this.reset();
    },
    destroy() {
      // TODO: add proper destroy method
      window.removeEventListener("resize", this.resetThrottled);
    },
    reset() {
      this.setDimensions();
      this.setOffset(this.activeSlide);
    },
    setDimensions() {
      this.frameWidth = this.$refs.frame.getBoundingClientRect().width;
      this.slidesWidth = this.slides.reduce(
        (summ, slide) => (summ += slide.getBoundingClientRect().width),
        0
      );
    },
    setInfinite() {
      const slides = this.slides;
      const front = slides.slice(0, this.infinite);
      const back = slides.slice(slides.length - this.infinite, slides.length);

      front.forEach(element => {
        const cloned = element.cloneNode(true);

        this.$refs.slides.appendChild(cloned);
      });

      back.reverse().forEach(element => {
        const cloned = element.cloneNode(true);

        this.$refs.slides.insertBefore(cloned, this.$refs.slides.firstChild);
      });
    },
    onTouchstart(event) {
      if (this.isTransitioning) {
        return;
      }

      const touches = event.touches ? event.touches[0] : event;
      const frame = this.$refs.frame;

      if (!event.touches) {
        event.preventDefault();
      }

      if (this.enableMouseEvents) {
        frame.addEventListener("mousemove", this.onTouchmove);
        frame.addEventListener("mouseup", this.onTouchend);
        frame.addEventListener("mouseleave", this.onTouchend);
      }

      frame.addEventListener("touchmove", this.onTouchmove);
      frame.addEventListener("touchend", this.onTouchend);

      const { pageX, pageY } = touches;

      touchOffset = {
        x: pageX,
        y: pageY
      };

      isScrolling = null;

      delta = {};
    },
    onTouchmove(event) {
      const touches = event.touches ? event.touches[0] : event;
      const { pageX, pageY } = touches;

      delta = {
        x: pageX - touchOffset.x,
        y: pageY - touchOffset.y
      };

      if (isScrolling === null) {
        isScrolling = Math.abs(delta.x) < Math.abs(delta.y);
      }

      if (!isScrolling && touchOffset) {
        event.preventDefault();
        this.setOffset(this.activeSlide, delta.x);
      }
    },
    onTouchend(event) {
      const frame = this.$refs.frame;

      event.preventDefault();

      frame.removeEventListener("mousemove", this.onTouchmove);
      frame.removeEventListener("mouseup", this.onTouchend);
      frame.removeEventListener("mouseleave", this.onTouchend);

      frame.removeEventListener("touchmove", this.onTouchmove);
      frame.removeEventListener("touchend", this.onTouchend);

      const difference = (delta.x / this.frameWidth) * 100;
      const direction = difference < 0;
      const slidedEnough = Math.abs(difference) >= 10;

      if (slidedEnough) {
        let index = this.activeSlide + (direction ? 1 : -1);
        this.slideTo(index);
      } else {
        this.slideTo(this.activeSlide);
      }

      touchOffset = null;
    },
    goToNext() {
      this.slideTo(null, true);
    },
    goToPrev() {
      this.slideTo(null, false);
    },
    goTo(index) {
      this.slideTo(index);
    },
    slideTo(index, direction) {
      let realIndex;
      const isFirstSlide = this.activeSlide === 0;

      if (typeof index !== "number") {
        index = this.activeSlide + (direction ? 1 : -1);
      }

      if (this.infinite) {
        if (this.isLastSlide && index > this.lastIndex) {
          realIndex = 0;
        } else if (isFirstSlide && index < 0) {
          realIndex = this.lastIndex;
        }
      } else {
        index = Math.min(Math.max(index, 0), this.lastIndex);
      }

      if (this.rewind && direction && this.isLastSlide) {
        index = 0;
      }

      this.translate(index, realIndex);
    },
    translate(index, realIndex) {
      if (this.isTransitioning) {
        return;
      }

      this.isTransitioning = true;

      const currentSlide = typeof realIndex === "number" ? realIndex : index;
      const rewind = this.rewind && index === 0 && this.isLastSlide;

      this.$emit("beforeChange", this.activeSlide, currentSlide);

      this.activeSlide = currentSlide;

      const slidesWrapper = this.$refs.slides;

      slidesWrapper.style.transition = `transform ${
        rewind ? DEFAULT.rewindSpeed : DEFAULT.slideSpeed
      }ms ease`;

      const self = this;
      slidesWrapper.addEventListener("transitionend", function cb(event) {
        slidesWrapper.style.transition = "";

        if (typeof realIndex === "number") {
          self.setOffset(currentSlide);
        }

        self.isTransitioning = false;
        self.$emit("afterChange", currentSlide);

        event.currentTarget.removeEventListener(event.type, cb);
      });

      this.setOffset(index);
    },
    setOffset(index, extraPx = 0) {
      this.coordinate =
        this.frameWidth * (index + (this.infinite || 0)) * -1 + extraPx;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/vars";

.wrapper {
  position: relative;
  user-select: none;
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

.frame {
  position: relative;
  font-size: 0;
  line-height: 0;
  overflow: hidden;
  white-space: nowrap;
}

.slides {
  display: flex;

  > * {
    flex: 1 0 100%;
  }
}

li {
  position: relative;
  display: inline-block;
}
</style>
